import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TrendingUp,
  MousePointerClick,
  DollarSign,
  Clock,
  Award,
  Users,
  BarChart3,
  PieChartIcon,
  Filter,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  CartesianGrid
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  purple: "#6B46C1",
  blue: "#3B82F6",
  emerald: "#10B981",
  amber: "#F59E0B",
  rose: "#F43F5E"
};

const PIE_COLORS = ["#6B46C1", "#3B82F6", "#10B981"];
const FUNNEL_COLORS = ["#6B46C1", "#7C3AED", "#8B5CF6", "#A78BFA", "#C4B5FD"];

const TIME_RANGES = [
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "12m", label: "Last 12 Months" },
];

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeRange, setTimeRange] = useState("30d");
  
  // Data states
  const [performance, setPerformance] = useState(null);
  const [referralsData, setReferralsData] = useState([]);
  const [earningsData, setEarningsData] = useState([]);
  const [planDistribution, setPlanDistribution] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [tierProgress, setTierProgress] = useState(null);

  const fetchAnalyticsData = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    
    try {
      const [perfRes, referralsRes, earningsRes, planRes, funnelRes, overviewRes] = await Promise.all([
        axios.get(`${API}/analytics/performance`, { withCredentials: true }),
        axios.get(`${API}/analytics/referrals?time_range=${timeRange}`, { withCredentials: true }),
        axios.get(`${API}/analytics/earnings?time_range=${timeRange}`, { withCredentials: true }),
        axios.get(`${API}/analytics/plan-distribution`, { withCredentials: true }),
        axios.get(`${API}/analytics/conversion-funnel`, { withCredentials: true }),
        axios.get(`${API}/dashboard/overview`, { withCredentials: true })
      ]);

      setPerformance(perfRes.data);
      setReferralsData(referralsRes.data);
      setEarningsData(earningsRes.data);
      setPlanDistribution(planRes.data);
      setFunnelData(funnelRes.data);
      setTierProgress(overviewRes.data.tier_progress);
      
      if (showRefreshing) toast.success("Analytics refreshed");
    } catch (error) {
      console.error("Analytics fetch error:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [timeRange]);

  useEffect(() => {
    document.title = "Analytics | GeniusList Partners";
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
  };

  const handleRefresh = () => {
    fetchAnalyticsData(true);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header skeleton */}
        <div className="flex justify-between items-center">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        
        {/* KPI Cards skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="bg-white border-slate-100">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-20 mb-3" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        
        {/* Charts skeleton */}
        <div className="grid lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-white border-slate-100">
              <CardContent className="p-6">
                <Skeleton className="h-6 w-40 mb-4" />
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const kpiMetrics = [
    {
      title: "Conversion Rate",
      value: `${performance?.conversion_rate || 0}%`,
      icon: TrendingUp,
      color: "text-purple-600 bg-purple-50"
    },
    {
      title: "Click-Through Rate",
      value: `${performance?.click_through_rate || 0}%`,
      icon: MousePointerClick,
      color: "text-blue-600 bg-blue-50"
    },
    {
      title: "Avg Commission",
      value: `$${performance?.avg_commission_per_referral || 0}`,
      icon: DollarSign,
      color: "text-emerald-600 bg-emerald-50"
    },
    {
      title: "Subscriber LTV",
      value: `$${performance?.subscriber_ltv || 0}`,
      icon: Clock,
      color: "text-amber-600 bg-amber-50"
    },
    {
      title: "Tier Progress",
      value: `${tierProgress?.progress_percent || 0}%`,
      subtitle: tierProgress?.next_tier ? `to ${tierProgress.next_tier}` : "Max tier!",
      icon: Award,
      color: "text-rose-600 bg-rose-50",
      isProgress: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header with Time Range Selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Performance Analytics</h2>
          <p className="text-sm text-slate-500">Track your referral performance and earnings</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-44" data-testid="time-range-select">
              <Filter className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpiMetrics.map((metric, idx) => {
          const Icon = metric.icon;
          return (
            <Card key={idx} className="bg-white border-slate-100 shadow-sm card-hover" data-testid={`kpi-card-${idx}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <p className="text-sm font-medium text-slate-500">{metric.title}</p>
                  <div className={`p-2 rounded-lg ${metric.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-slate-900">{metric.value}</p>
                {metric.subtitle && (
                  <p className="text-xs text-slate-500 mt-1">{metric.subtitle}</p>
                )}
                {metric.isProgress && (
                  <Progress value={tierProgress?.progress_percent || 0} className="h-1.5 mt-2" />
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Charts Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Referral Growth Over Time */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Referral Growth Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={referralsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total" 
                    name="Total Referrals"
                    stroke={COLORS.purple}
                    strokeWidth={2}
                    dot={{ fill: COLORS.purple, strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="active" 
                    name="Active Referrals"
                    stroke={COLORS.emerald}
                    strokeWidth={2}
                    dot={{ fill: COLORS.emerald, strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.purple }}></div>
                <span className="text-xs text-slate-600">Total Referrals</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.emerald }}></div>
                <span className="text-xs text-slate-600">Active Referrals</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Earnings Over Time */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              Earnings Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={earningsData}>
                  <defs>
                    <linearGradient id="earningsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.emerald} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={COLORS.emerald} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(val) => `$${val}`}
                  />
                  <Tooltip 
                    formatter={(value) => [`$${value.toLocaleString()}`, "Commission"]}
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="earnings" 
                    stroke={COLORS.emerald}
                    strokeWidth={2}
                    fill="url(#earningsGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {earningsData.length > 0 && (
              <div className="flex justify-center mt-3">
                <div className="px-4 py-2 bg-emerald-50 rounded-lg">
                  <span className="text-xs text-emerald-600">
                    Total: ${earningsData.reduce((sum, d) => sum + (d.earnings || 0), 0).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plan Distribution */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-blue-600" />
              Plan Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 flex items-center">
              <div className="w-1/2 h-full">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={planDistribution?.distribution || []}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={4}
                      dataKey="value"
                      nameKey="name"
                    >
                      {(planDistribution?.distribution || []).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name) => [value, name]}
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-3">
                {(planDistribution?.distribution || []).map((plan, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: PIE_COLORS[idx] }}
                      />
                      <span className="text-sm font-medium text-slate-700">{plan.name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">{plan.value}</p>
                      <p className="text-xs text-slate-500">{plan.percent}%</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-600" />
                Conversion Funnel
              </CardTitle>
              {funnelData?.overall_conversion && (
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {funnelData.overall_conversion}% Overall
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(funnelData?.stages || []).map((stage, idx) => {
                const widthPercent = stage.rate;
                
                return (
                  <div key={idx} className="relative" data-testid={`funnel-stage-${idx}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-slate-700">{stage.stage}</span>
                      <span className="text-slate-500">
                        {stage.count.toLocaleString()} 
                        <span className="ml-1 text-xs">({stage.rate}%)</span>
                      </span>
                    </div>
                    <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
                      <div 
                        className="h-full rounded-lg transition-all duration-500 flex items-center justify-end pr-2"
                        style={{ 
                          width: `${Math.max(widthPercent, 5)}%`,
                          backgroundColor: FUNNEL_COLORS[idx]
                        }}
                      >
                        {widthPercent >= 20 && (
                          <span className="text-xs font-medium text-white">{stage.rate}%</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Conversion insights */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-700">
                <strong>Insight:</strong> {funnelData?.stages?.[4]?.count || 0} out of {funnelData?.stages?.[0]?.count || 0} clicks 
                converted to paid subscribers ({funnelData?.overall_conversion || 0}% conversion rate)
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Traffic Sources */}
      <Card className="bg-white border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">
            Traffic Sources
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(performance?.traffic_sources || []).map((source, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{source.name}</span>
                  <span className="text-lg font-bold text-slate-900">{source.value}%</span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${source.value}%`,
                      backgroundColor: PIE_COLORS[idx % PIE_COLORS.length]
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
